import type {
	IDateFilterParams,
	IMultiFilterDef,
	IMultiFilterParams,
	INumberFilterParams,
	ISetFilterParams,
	ITextFilterParams
} from "ag-grid-enterprise";
import type { ColDef } from "~/components/grid/AgGrid";

export type FilterType = "bool" | "date" | "dateOnly" | "number" | "set" | "text";

export type ColDefField<TData> = NonNullable<ColDef<TData>["field"]>;

export const STANDARD_FILTER_PARAMS = {
	date: {
		buttons: ["reset", "apply"],
		closeOnApply: true,
		defaultOption: "equals",
		filterOptions: ["equals", "notEqual", "lessThan", "greaterThan", "inRange", "blank", "notBlank"],
		maxNumConditions: 2
	} satisfies IDateFilterParams,
	number: {
		allowedCharPattern: "\\d\\-\\.",
		buttons: ["reset", "apply"],
		closeOnApply: true,
		debounceMs: 500,
		defaultOption: "equals",
		filterOptions: [
			"equals",
			"notEqual",
			"lessThan",
			"greaterThan",
			"lessThanOrEqual",
			"greaterThanOrEqual",
			"inRange",
			"blank",
			"notBlank"
		],
		maxNumConditions: 2
	} satisfies INumberFilterParams,
	set: {
		buttons: ["reset", "apply"],
		closeOnApply: true,
		defaultToNothingSelected: false,
		refreshValuesOnOpen: true,
		suppressMiniFilter: false
	} satisfies ISetFilterParams,
	text: {
		buttons: ["reset", "apply"],
		closeOnApply: true,
		debounceMs: 500,
		defaultOption: "contains",
		filterOptions: ["contains", "notContains", "equals", "notEqual", "startsWith", "endsWith", "blank", "notBlank"],
		maxNumConditions: 2
	} satisfies ITextFilterParams
};

const withoutCloseOnApply = <TParams extends { closeOnApply?: boolean }>({ closeOnApply, ...rest }: TParams) => rest;

export const SSRM_TYPED_FILTER_PARAMS = {
	number: {
		...withoutCloseOnApply(STANDARD_FILTER_PARAMS.number),
		buttons: [],
		debounceMs: 400
	} satisfies INumberFilterParams,
	set: { ...withoutCloseOnApply(STANDARD_FILTER_PARAMS.set), buttons: [], debounceMs: 400 } satisfies ISetFilterParams,
	text: {
		...withoutCloseOnApply(STANDARD_FILTER_PARAMS.text),
		buttons: [],
		debounceMs: 400
	} satisfies ITextFilterParams
};

export const SET_TEXT_MULTI_FILTER_TEXT_INDEX = 0;

export const SET_TEXT_MULTI_FILTER_SET_INDEX = 1;

export const createTextAndSetFilters = <TData, TValue>(
	textFilterParams: ITextFilterParams,
	setFilterParams: Partial<ISetFilterParams<TData, TValue>>
): IMultiFilterDef[] => {
	const filters: IMultiFilterDef[] = [];
	filters[SET_TEXT_MULTI_FILTER_TEXT_INDEX] = { filter: "agTextColumnFilter", filterParams: textFilterParams };
	filters[SET_TEXT_MULTI_FILTER_SET_INDEX] = { filter: "agSetColumnFilter", filterParams: setFilterParams };
	return filters;
};

// ag-grid 35.1 MultiFloatingFilterComp merges every sub-filter's filterParams into one shared object, last one wins.
export const createSsrmTextAndSetFilterParams = <TData, TValue>(
	setFilterParams: Partial<ISetFilterParams<TData, TValue>>
): IMultiFilterParams => ({
	filters: createTextAndSetFilters(SSRM_TYPED_FILTER_PARAMS.text, {
		...SSRM_TYPED_FILTER_PARAMS.set,
		...setFilterParams
	})
});

const filterComponent: Record<Exclude<FilterType, "bool">, string> = {
	date: "agDateColumnFilter",
	dateOnly: "agDateColumnFilter",
	number: "agNumberColumnFilter",
	set: "agSetColumnFilter",
	text: "agTextColumnFilter"
};

const filterParamsFor = (filterType: Exclude<FilterType, "bool">) =>
	filterType === "dateOnly" ? STANDARD_FILTER_PARAMS.date : STANDARD_FILTER_PARAMS[filterType];

const createFilteredColumn = <TData>(
	field: ColDefField<TData>,
	headerName: string,
	filterType: FilterType,
	additionalConfig?: Partial<ColDef<TData>>
): ColDef<TData> => {
	if (filterType === "bool") {
		return {
			field,
			filter: "agSetColumnFilter",
			filterParams: { ...STANDARD_FILTER_PARAMS.set, values: [true, false] },
			headerName,
			...additionalConfig
		};
	}

	return {
		field,
		filter: filterComponent[filterType],
		filterParams: filterParamsFor(filterType),
		headerName,
		...additionalConfig
	};
};

const isNumericColumnType = (type: ColDef["type"]): boolean => {
	if (type === "numericColumn") return true;
	if (Array.isArray(type)) return type.includes("numericColumn") || type.includes("currency");
	return false;
};

const toSingleColumnFilter = <TData>(colDef: ColDef<TData>): ColDef<TData> => {
	if (colDef.filter !== undefined) return colDef;

	const numeric = isNumericColumnType(colDef.type);
	return {
		...colDef,
		filter: numeric ? "agNumberColumnFilter" : "agTextColumnFilter",
		filterParams: numeric ? STANDARD_FILTER_PARAMS.number : STANDARD_FILTER_PARAMS.text
	};
};

export const withSingleColumnFilters = <TData>(colDefs: ColDef<TData>[]): ColDef<TData>[] =>
	colDefs.map(toSingleColumnFilter);

export default createFilteredColumn;
